import { inject, Injectable } from '@angular/core';
import {
  Auth,
  authState,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User as FirebaseUser,
} from '@angular/fire/auth';
import {
  getMultiFactorResolver,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
} from 'firebase/auth';
import {
  collection,
  doc,
  Firestore,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Tenant, User } from '../models';
import { AuthStore } from './auth.store';

export interface SignInOptions {
  tenantDomain?: string;
  mode?: 'tenant' | 'platform';
  resolvedTenant?: Tenant | null;
}

export interface TwoFactorSignInChallenge {
  resolver: MultiFactorResolver;
  hints: MultiFactorInfo[];
  options: SignInOptions;
  email: string;
}

export interface TotpEnrollmentSession {
  secret: TotpSecret;
  otpauthUrl: string;
  tenant: Tenant;
  options: SignInOptions;
  email: string;
}

export class TwoFactorChallengeRequiredError extends Error {
  readonly code = 'auth/multi-factor-auth-required';

  constructor(readonly challenge: TwoFactorSignInChallenge) {
    super('Two-factor authentication required.');
  }
}

export class TwoFactorEnrollmentRequiredError extends Error {
  readonly code = 'auth/mfa-enrollment-required';

  constructor(readonly enrollment: TotpEnrollmentSession) {
    super('Two-factor authentication enrollment required.');
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(Auth);
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly router = inject(Router);
  private readonly store = inject(AuthStore);
  private readonly tenantDomainKey = 'talentpro.tenantDomain';
  private readonly mfaEnforced = false;

  readonly authState$: Observable<FirebaseUser | null> = authState(this.auth);

  async initialize(): Promise<void> {
    return new Promise((resolve) => {
      const unsubscribe = this.auth.onAuthStateChanged(async (firebaseUser) => {
        if (firebaseUser) {
          try {
            await this.loadUserData(firebaseUser);
          } catch (error) {
            console.error('Failed to load user data:', error);
            this.store.reset();
          }
        } else {
          this.store.reset();
        }
        this.store.setLoading(false);
        this.store.setInitialized(true);
        unsubscribe();
        resolve();
      });
    });
  }

  private async loadUserData(
    firebaseUser: FirebaseUser,
    options: SignInOptions = {}
  ): Promise<void> {
    this.store.setPlatformAdmin(false);
    const email = firebaseUser.email ?? '';
    const platformProfile = await this.getPlatformAdminProfile(firebaseUser.uid);

    if (platformProfile) {
      const user: User = {
        id: firebaseUser.uid,
        email: platformProfile.email ?? email,
        displayName: platformProfile.displayName ?? email.split('@')[0] ?? 'Admin',
        role: 'platform_admin',
        roleId: null,
        status: 'active',
        tenantId: 'platform',
        createdAt: platformProfile.createdAt ?? new Date(),
        lastLoginAt: new Date(),
      };
      this.store.setUser(user);
      this.store.setTenant(null);
      this.store.setPlatformAdmin(true);
      return;
    }

    if (options.mode === 'platform') {
      throw new Error('platform/not-authorized');
    }

    const tenant =
      options.resolvedTenant ?? (await this.resolveTenantForEmail(email, options.tenantDomain));
    if (!tenant) {
      throw new Error('tenant/not-found');
    }

    try {
      const token = await firebaseUser.getIdTokenResult();
      console.info('Auth claims at login:', token.claims, 'Resolved tenant:', tenant.id);
    } catch (error) {
      console.warn('Unable to read auth claims at login:', error);
    }

    const requireTwoFactorAuth = tenant.settings?.requireTwoFactorAuth ?? false;
    const enrolledFactors = multiFactor(firebaseUser).enrolledFactors.length;
    if (this.mfaEnforced && requireTwoFactorAuth && enrolledFactors === 0) {
      await signOut(this.auth);
      this.store.reset();
      throw new Error('auth/mfa-enrollment-required');
    }

    const userDocRef = doc(
      this.firestore,
      `tenants/${tenant.id}/users/${firebaseUser.uid}`
    );
    let userSnap;
    try {
      userSnap = await getDoc(userDocRef);
    } catch (error) {
      console.error('Failed to fetch user profile:', {
        tenantId: tenant.id,
        userId: firebaseUser.uid,
        error,
      });
      throw error;
    }

    if (!userSnap.exists()) {
      const migrated = await this.tryMigrateUserProfile(tenant.id, firebaseUser);
      if (migrated) {
        this.store.setUser(migrated);
        await this.loadTenant(tenant.id);
        this.setStoredTenantDomain(tenant.domain);
        // Ensure custom claims are synced for storage rules
        await this.syncUserClaims(tenant.id);
        return;
      }
    } else {
      const userData = userSnap.data();
      const user: User = {
        id: firebaseUser.uid,
        email: userData['email'],
        displayName: userData['displayName'],
        role: userData['role'],
        roleId: userData['roleId'] ?? null,
        status: userData['status'],
        tenantId: tenant.id,
        createdAt: userData['createdAt']?.toDate(),
        lastLoginAt: userData['lastLoginAt']?.toDate() ?? null,
      };
      this.store.setUser(user);
      await this.loadTenant(tenant.id);
      this.setStoredTenantDomain(tenant.domain);
      // Ensure custom claims are synced for storage rules
      await this.syncUserClaims(tenant.id);
      void this.markUserActive(tenant.id, firebaseUser.uid, user.status);
      return;
    }

    if (environment.production || !environment.enableDevAdminBootstrap) {
      throw new Error('auth/user-profile-not-found');
    }

    const user: User = {
      id: firebaseUser.uid,
      email,
      displayName: firebaseUser.displayName ?? email.split('@')[0] ?? 'User',
      role: 'hr_admin',
      roleId: 'sys-hr-admin',
      status: 'active',
      tenantId: tenant.id,
      createdAt: new Date(),
      lastLoginAt: new Date(),
    };
    await setDoc(userDocRef, {
      email: user.email,
      displayName: user.displayName,
      role: user.role,
      roleId: user.roleId,
      status: user.status,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    });
    this.store.setUser(user);
    await this.loadTenant(tenant.id);
    this.setStoredTenantDomain(tenant.domain);
    // Ensure custom claims are synced for storage rules
    await this.syncUserClaims(tenant.id);
    void this.markUserActive(tenant.id, firebaseUser.uid, user.status);
  }

  private async markUserActive(
    tenantId: string,
    userId: string,
    status?: string | null
  ): Promise<void> {
    try {
      const userRef = doc(this.firestore, `tenants/${tenantId}/users/${userId}`);
      const updates: Record<string, unknown> = {
        lastLoginAt: serverTimestamp(),
      };
      if (status === 'invited') {
        updates['status'] = 'active';
      }
      await updateDoc(userRef, updates);
    } catch (error) {
      console.warn('Failed to update login status:', error);
    }
  }

  private async getPlatformAdminProfile(
    userId: string
  ): Promise<{ email?: string; displayName?: string; createdAt?: Date } | null> {
    try {
      const adminRef = doc(this.firestore, `platformAdmins/${userId}`);
      const snap = await getDoc(adminRef);
      if (snap.exists()) {
        const data = snap.data();
        return {
          email: data['email'] as string | undefined,
          displayName: data['displayName'] as string | undefined,
          createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.(),
        };
      }
    } catch (error) {
      console.warn('Platform admin lookup blocked:', error);
      return null;
    }

    const email = this.auth.currentUser?.email?.toLowerCase();
    if (!email) return null;

    try {
      const adminsRef = collection(this.firestore, 'platformAdmins');
      const q = query(adminsRef, where('email', '==', email));
      const match = await getDocs(q);
      const docSnap = match.docs[0];
      if (!docSnap) return null;

      const data = docSnap.data();
      return {
        email: data['email'] as string | undefined,
        displayName: data['displayName'] as string | undefined,
        createdAt: (data['createdAt'] as { toDate?: () => Date })?.toDate?.(),
      };
    } catch (error) {
      console.warn('Platform admin email lookup blocked:', error);
      return null;
    }
  }

  private async resolveTenantForEmail(
    email: string,
    fallbackDomain?: string
  ): Promise<Tenant | null> {
    const emailDomain = email.split('@')[1]?.toLowerCase() ?? '';
    const storedDomain = this.getStoredTenantDomain();
    const preferred = fallbackDomain?.toLowerCase() ?? storedDomain;

    if (emailDomain) {
      const tenant = await this.findTenantByDomain(emailDomain);
      if (tenant) return tenant;
    }

    if (preferred) {
      return this.findTenantByDomain(preferred);
    }

    return null;
  }

  private async findTenantByDomain(domain: string): Promise<Tenant | null> {
    const tenantsRef = collection(this.firestore, 'tenants');
    const q = query(tenantsRef, where('domain', '==', domain.toLowerCase()), limit(1));
    const snap = await getDocs(q);

    const docSnap = snap.docs[0];
    if (!docSnap) return null;

    const data = docSnap.data();
    return {
      id: docSnap.id,
      name: data['name'],
      domain: data['domain'],
      settings: data['settings'],
      subscription: data['subscription'],
      createdAt: data['createdAt']?.toDate(),
      updatedAt: data['updatedAt']?.toDate(),
    } as Tenant;
  }

  private async tryMigrateUserProfile(
    tenantId: string,
    firebaseUser: FirebaseUser
  ): Promise<User | null> {
    const email = firebaseUser.email ?? '';
    if (!email) return null;

    const usersRef = collection(this.firestore, `tenants/${tenantId}/users`);
    const lowerEmail = email.toLowerCase();
    const primarySnap = await getDocs(query(usersRef, where('email', '==', lowerEmail)));
    const existing = primarySnap.docs[0];
    if (!existing) {
      const fallbackSnap = await getDocs(query(usersRef, where('email', '==', email)));
      if (!fallbackSnap.docs[0]) return null;
      return this.migrateUserDoc(tenantId, firebaseUser, fallbackSnap.docs[0]);
    }

    return this.migrateUserDoc(tenantId, firebaseUser, existing);
  }

  private async migrateUserDoc(
    tenantId: string,
    firebaseUser: FirebaseUser,
    existing: { id: string; data: () => Record<string, unknown> }
  ): Promise<User> {
    const data = existing.data();
    const role = (data['role'] as User['role']) ?? 'employee';
    const roleId = (data['roleId'] as string | null) ?? null;
    const displayName =
      (data['displayName'] as string) ?? firebaseUser.displayName ?? (firebaseUser.email ?? '').split('@')[0] ?? 'User';

    await setDoc(doc(this.firestore, `tenants/${tenantId}/users/${firebaseUser.uid}`), {
      email: (firebaseUser.email ?? '').toLowerCase(),
      displayName,
      role,
      roleId,
      status: data['status'] ?? 'active',
      createdAt: data['createdAt'] ?? serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      migratedFromId: existing.id,
    });

    const createdAt =
      (data['createdAt'] as { toDate?: () => Date } | undefined)?.toDate?.() ?? new Date();

    return {
      id: firebaseUser.uid,
      email: firebaseUser.email ?? '',
      displayName,
      role,
      roleId,
      status: (data['status'] as User['status']) ?? 'active',
      tenantId,
      createdAt,
      lastLoginAt: new Date(),
    };
  }

  private setStoredTenantDomain(domain: string): void {
    localStorage.setItem(this.tenantDomainKey, domain.toLowerCase());
  }

  private getStoredTenantDomain(): string | null {
    return localStorage.getItem(this.tenantDomainKey);
  }

  private hasEnrolledSecondFactor(firebaseUser: FirebaseUser): boolean {
    return multiFactor(firebaseUser).enrolledFactors.length > 0;
  }

  private async startTotpEnrollment(
    firebaseUser: FirebaseUser,
    tenant: Tenant,
    options: SignInOptions,
    email: string
  ): Promise<TotpEnrollmentSession> {
    try {
      const session = await multiFactor(firebaseUser).getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      const otpauthUrl = secret.generateQrCodeUrl(firebaseUser.email ?? email, tenant.name);
      return {
        secret,
        otpauthUrl,
        tenant,
        options,
        email,
      };
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (code === 'auth/operation-not-allowed') {
        throw new Error('auth/mfa-provider-not-enabled');
      }
      throw error;
    }
  }

  private async loadTenant(tenantId: string): Promise<void> {
    const tenantDocRef = doc(this.firestore, `tenants/${tenantId}`);
    const tenantSnap = await getDoc(tenantDocRef);

    if (tenantSnap.exists()) {
      const data = tenantSnap.data();
      const tenant: Tenant = {
        id: tenantSnap.id,
        name: data['name'],
        domain: data['domain'],
        settings: data['settings'],
        subscription: data['subscription'],
        createdAt: data['createdAt']?.toDate(),
        updatedAt: data['updatedAt']?.toDate(),
      };
      this.store.setTenant(tenant);
    }
  }

  async signIn(email: string, password: string, options: SignInOptions = {}): Promise<void> {
    this.store.setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      let resolvedTenant: Tenant | null = null;
      if (options.mode !== 'platform') {
        resolvedTenant = await this.resolveTenantForEmail(email, options.tenantDomain);
        if (!resolvedTenant) {
          await signOut(this.auth);
          this.store.reset();
          throw new Error('tenant/not-found');
        }
        const requireTwoFactorAuth = resolvedTenant.settings?.requireTwoFactorAuth ?? false;
        if (
          this.mfaEnforced &&
          requireTwoFactorAuth &&
          !this.hasEnrolledSecondFactor(credential.user)
        ) {
          const enrollment = await this.startTotpEnrollment(
            credential.user,
            resolvedTenant,
            options,
            email
          );
          throw new TwoFactorEnrollmentRequiredError(enrollment);
        }
      }
      try {
        await this.loadUserData(credential.user, {
          ...options,
          resolvedTenant,
        });
      } catch (error) {
        await signOut(this.auth);
        this.store.reset();
        throw error;
      }
    } catch (error) {
      const code = (error as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(this.auth, error as MultiFactorError);
        throw new TwoFactorChallengeRequiredError({
          resolver,
          hints: resolver.hints,
          options,
          email,
        });
      }
      throw error;
    } finally {
      this.store.setLoading(false);
    }
  }

  async completeTotpEnrollment(session: TotpEnrollmentSession, code: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      throw new Error('auth/no-current-user');
    }
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(session.secret, code.trim());
    await multiFactor(firebaseUser).enroll(assertion, 'Authenticator app');
    await this.loadUserData(firebaseUser, {
      ...session.options,
      resolvedTenant: session.tenant,
    });
  }

  async completeTwoFactorSignIn(challenge: TwoFactorSignInChallenge, code: string): Promise<void> {
    const totpHint = challenge.hints.find(
      (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
    );
    if (!totpHint) {
      throw new Error('auth/mfa-unsupported-factor');
    }
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, code.trim());
    const credential = await challenge.resolver.resolveSignIn(assertion);
    await this.loadUserData(credential.user, challenge.options);
  }

  async signUp(email: string, password: string): Promise<void> {
    this.store.setLoading(true);
    try {
      await createUserWithEmailAndPassword(this.auth, email, password);
    } finally {
      this.store.setLoading(false);
    }
  }

  async signOut(): Promise<void> {
    const isPlatformAdmin = this.store.isPlatformAdmin();
    await signOut(this.auth);
    this.store.reset();
    await this.router.navigate([isPlatformAdmin ? '/platform/login' : '/auth/login']);
  }

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  async refreshToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (user) {
      return user.getIdToken(true);
    }
    return null;
  }

  async syncUserClaims(tenantId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    try {
      const syncClaims = httpsCallable<{ tenantId: string }, { tenantId: string; role: string }>(
        this.functions,
        'syncUserClaims'
      );
      await syncClaims({ tenantId });
      // Force refresh the ID token to get the updated claims
      await user.getIdToken(true);
    } catch (error) {
      console.warn('Failed to sync user claims:', error);
    }
  }

  subscribeToUserChanges(): () => void {
    if (this.store.isPlatformAdmin()) {
      return () => {};
    }

    const tenantId = this.store.tenantId();
    const userId = this.store.userId();

    if (!tenantId || !userId) {
      return () => {};
    }

    const userDocRef = doc(
      this.firestore,
      `tenants/${tenantId}/users/${userId}`
    );

    return onSnapshot(userDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const currentUser = this.store.user();
        if (currentUser) {
          this.store.setUser({
            ...currentUser,
            displayName: data['displayName'],
            role: data['role'],
            roleId: data['roleId'] ?? null,
            status: data['status'],
          });
        }
      }
    });
  }
}
